/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2017, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../features/ProfileFeatureTracker',
	'../../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../../lib/@waca/dashboard-common/dist/utils/ObjectHelper'
], function (ProfileFeatureTracker, Class, ObjectHelper) {

	const DashboardController = Class.extend({

		init: function (options) {
			DashboardController.inherited('init', this, arguments);
			this.view = options.view;

			// TODO -- function using the following too should go away.
			// We can access the features Glass and Application directly to get the same functionality
			this.glass = options.glass;
			this._settings = options.settings;

			this.eventRouter = options.eventRouter;
			this.services = options.services;
			this.widgetRegistry = options.widgetRegistry;
			this.FrozenWidgetRegistry = null;

			const optionsSetting = this._settings.get('applicationSettings').options;
			this.collectionsConfig =  optionsSetting && optionsSetting.collections || {};
			this.config =  optionsSetting && optionsSetting.config || {};

			this._createProfileFeatureTracker(options.profiles);
		},

		_createProfileFeatureTracker: function(profileList) {
			this.profileFeatureTracker = new ProfileFeatureTracker(profileList);
			this.profileFeatureTracker.enable( this._isProfileFeatureLoadingEnabled() );
		},

		_isProfileFeatureLoadingEnabled: function() {
			let enableProfileFeatureLoading = false;
			const featureChecker = this.glass.getCoreService('.FeatureChecker');
			if ( featureChecker && featureChecker.checkValue ) {
				enableProfileFeatureLoading = !featureChecker.checkValue('dashboard', 'profilefeatureloading', 'disabled');
			}
			return enableProfileFeatureLoading;
		},

		updateCurrentProfile(params) {
			return this.profileFeatureTracker.updateCurrentProfile(params);
		},

		getCurrentProfile() {
			return this.profileFeatureTracker.getCurrentProfile();
		},

		onPageReady: function(isAuthoringMode) {
			const canvas = this.featureLoader.getFeature('Canvas.internal');
			const rootContent = canvas && canvas.getContentInstance();
			if( rootContent ) {
				this.profileFeatureTracker.setRootContent(rootContent);
				let profiles = ['consume'];
				if(isAuthoringMode) {
					profiles.push('authoring');
				}
				return this.profileFeatureTracker.updateCurrentProfile( { add: profiles } );
			}
			return Promise.resolve(true);
		},

		_changeProfile: function(mode) {
			if(mode ==='authoring') {
				return this.profileFeatureTracker.updateCurrentProfile( { add:['authoring'] } );
			} else if( mode === 'consumption') {
				return this.profileFeatureTracker.updateCurrentProfile( { remove:['authoring'], add: ['consume'] } );
			} else {
				return Promise.reject( 'unknown mode ' + mode  );
			}
		},

		getFeature(name) {
			let feature;
			if (this.featureLoader) {
				feature = this.featureLoader.getFeature(name);
			}
			if (!feature) {
				try {
					feature = this.getDashboardCoreSvc(name);
				} catch (e) {
					//console.log('DashboardController.getFeature', e);
					// Ignore if getDashboardCoreSvc threw a missing feature error.  getFeature should never throw a fault.
					// and won't once we clean up the old service stuff
				}
			}
			return feature;
		},

		setDirty(dirty){
			// Sync dirty state to undoredo controller
			if (this.view.boardController && this.view.boardController.undoRedoController) {
				this.view.boardController.undoRedoController.setDirty(dirty);
			}
			this.view.trigger('change:dirty', {
				value: dirty
			});
			this.view.eventRouter.trigger('change:dirty', {
				value: dirty
			});
		},

		setServices: function(services){
			this.services = services;
		},

		setFeatureLoader: function(featureLoader){
			this.featureLoader = featureLoader;
			this.profileFeatureTracker.setDashboardFeatureLoader(this.featureLoader);

			featureLoader.registerFeature('ProfileFeatureTracker', this.profileFeatureTracker, { profiles: ['core'] });
		},

		/**
		 * Return the current content view
		 */
		getCurrentContentView: function () {
			return this.view;
		},

		/**
		* Return the canvas API. This method might retrun null if the canvas is not ready yet
		*/
		getCanvas: function(){
			// The deprecated canvas contains all the deprecated api + the proper public apis
			return this.view.getDeprecatedCanvas();
		},

		/**
		* Return a promise that will be resolved with the canvas API
		*/
		getCanvasWhenReady: function(){
			// The deprecated canvas contains all the deprecated api + the proper public apis
			return this.view.getDeprecatedCanvasWhenReady();
		},

		isDevInstall: function(){
			return this.glass.isDevInstall();
		},

		/**
		*	Add a new widget to the canvas or new content to selected widgets
		*
		* @param {object} - options containing the widget to be added
		*/
		addContentToCanvas: function(options){
			this.view.boardController.addDataItemsOrAddWidget(options);
		},

		/**
		*	Return the application name
		*/
		getApplicationName: function(){
			const name = this._settings.get('applicationName');
			return name && name.toLowerCase ? name.toLowerCase() : name;
		},

		/**
		*	Return the localized application name
		*/
		getApplicationLabel: function() {
			return this.view.getApplicationLabel();
		},

		/**
		 * Reload the dashboard using a give json specified
		 * @param {object} JSONSpec An object that represents a dashboard spec
		 * @param {object} extOptions extra options to use when re-initializing BaseBoardView
		 * @returns {Promise} The resolved object is the new dashboardApi instance from the reinitialization of the dashboad from the given spec
		 */
		reloadFromJSONSpec: function(JSONspec, extOptions) {
			return this.view.reloadFromJSONSpec(JSONspec, extOptions);
		},

		/**
		*	Return the CDN url
		*/
		getCDNUrl: function(){
			return this.view.getCDNUrl();
		},

		/**
		 * Get layout
		 *
		 * @param {string} id - layout id
		 *
		 * @return {Layout} layout
		 *
		 * @deprecated Use getCanvas().getLayout instead
		 */
		getLayout: function(id) {
			return this.view.boardController.layoutController.getView(id);
		},

		/**
		* Glassify the options a.k.a - make an options object ready to be passed to a glass dependant component
		*
		* @param {object} options to be glassified.
		*
		*/
		prepareGlassOptions: function(options){
			this.glass.prepareGlassOptions(options);
		},

		/**
		* Open a glass application view
		*
		* @param {string} name - application anme
		* @param {object} options -  application options.
		*
		*/
		openGlassApplication: function(name, options){
			return this.glass.openApplication(name, options);
		},

		/**
		* Close a glass application view
		*
		* @param {string} name - application name
		* @param {object} id -  id of the application being closed.
		* @param {object} options -  application options.
		*
		*/
		closeGlassApplication: function(name, id, options){
			return this.glass.closeApplication(name, id, options);
		},

		/**
		* Return a collection config specified through the perspective
		* @param {string} name collection name
		* @return {object} config value
		*/
		getCollectionConfig: function(name){
			return this.collectionsConfig[name];
		},

		/**
		* Return a config specified through the perspective
		* @param {string} config name
		* @return {object} config value
		*/
		getAppConfig: function(name){
			return this.config[name];
		},

		/**
		* Return a dashboard service
		* @param {string} name service name
		* @return {Promise} - resolved with the service object
		*/
		getDashboardSvc: function(name){
			return this.services.getSvc(name);
		},

		/**
		* Register a dashboard service
		* @param {string} name service name
		* @param {Object} service service object
		*
		*/
		registerDashboardSvc: function(name, service){
			return this.services.register(name, service);
		},

		/**
		* Return a dashboard core service
		* @param {string} name service name
		* @return {Object} - Service object
		*/
		getDashboardCoreSvc: function(name){
			return this.services.getSvcSync(name);
		},

		/**
		* Return a glass service
		* @param {string} name service name
		* @return {Promise} - resolved with the service object
		*/
		getGlassSvc: function(name){
			return this.glass.getService(name);
		},

		/**
		* Return a glass core service
		* @param {string} name service name
		* @return {Object} - Service object
		*/
		getGlassCoreSvc: function(name){
			return this.glass.getCoreService(name);
		},

		/**
		*	Show an error message
		* @param {string} message
		* @param {string} title
		*/
		showErrorMessage: function(message, title){
			return this.glass.showErrorMessage(message, title);
		},

		/**
		*	Show a message in the UI
		* @param {string} message
		*/
		showMessage: function(msg, title, type, buttons, width, callback, htmlContent, className ) {
			return this.glass.showMessage(msg, title, type, buttons, width, callback, htmlContent, className);
		},

		/**
		*	Show a toast message in the UI
		* @param {string} message
		* @param {object} options - toast options
		* @deprecated replaced by Glass feature showToast
		*/
		showToast: function(message, options) {
			return this.glass.showToast(message, options);
		},

		/**
		*	Show a glass slideout
		* @param {object} options - slideout options
		*/
		showSlideOut: function(options){
			return this.glass.showSlideOut(options);
		},

		/**
		*	Show a glass context menu
		* @param {object} options - context menu options
		*/
		showContextMenu: function(options){
			return this.glass.showContextMenu(options);
		},

		/**
		*	Find a glass plugin
		* @param {string} name - plugin name
		* @return {object} glass plugin object
		*/
		findGlassPlugin: function(name){
			return this.glass.getPlugin(name);
		},

		/**
		*	Find a glass collection
		* @param {string} name - plugin name
		* @return {Promise} promise resolved with the collection
		*/
		findGlassCollection: function(name){
			return Promise.resolve(this._settings.get(name));
		},

		/**
		*	Open a glass app view
		* @param {string} name
		* @param {object} options
		*/
		openAppView: function(name, options){
			return this.glass.openApplication(name, options);
		},

		/**
		 * @deprecated Use getCanvas().getSelectedWidgets() instead
		 */
		getSelectedWidgets: function() {
			return this.view.getSelectedWidgets();
		},

		/**
		*	Set the permissions on the dashboard
		* @param {object} permissions
		*/
		setPermissions: function(permissions){
			this.view.setPermissions(permissions);
		},

		/**
		 * Checks for write access to the dashboard
		 */
		canAuthor: function () {
			return this.view.canAuthor();
		},

		/**
		 * Destroy the dashboard - clean up
		 */
		destroy: function() {
			if (this.services) {
				this.services.destroy();
			}

			if (this.featureLoader) {
				this.featureLoader.destroy();
			}

			this.view = null;
			this.eventRouter = null;
			this.services = null;
			this.collectionsConfig = null;
			this.config = null;
			this.widgetRegistry = null;
			this.services = null;
			this.featureLoader = null;
		},

		/**
		 * Map between API event names and internal event names
		 */
		eventMap: {
			dirty: 'change:dirty'
		},

		/**
		 * Register an event handler for dashboard events
		 * @param {string} eventName Name of the dashboard event
		 * @param {function} handler Event handler to be called when the event occurrs
		 */
		on: function(eventName, callback, context) {
			if (!eventName) {
				return;
			}
			var name = this.eventMap[eventName] || eventName;
			return this.eventRouter.on(name, callback, context);
		},

		/**
		 * Unregister an event handler for dashboard events
		 * @param {string} eventName Name of the dashboard event
		 * @param {function} handler Event handler to be called when the event occurrs
		 */
		off: function(eventName, callback, context) {
			if (!eventName) {
				return;
			}
			var name = this.eventMap[eventName] || eventName;
			this.eventRouter.off(name, callback, context);
		},

		/**
		 * Trigger a dashboard event
		 * @param {string} eventName Name of the dashboard event
		 * @param {object} payload Event payload
		 */
		triggerDashboardEvent: function(eventName, payload) {
			if (!eventName) {
				return;
			}
			var name = this.eventMap[eventName] || eventName;
			this.eventRouter.trigger(name, payload);
		},

		/**
		 * Relink an existing source to a new module.
		 * Widgets that reference this source will reload after the relink is finished.
		 * @param {String} sourceInfo.id Id of the source to relink.  This id is passed to the caller in the relink:clicked event
		 * @param {String=} sourceInfo.cognosAssetId Cognos Content Story ID.
		 * @param {String=} sourceInfo.module The module definition
		 * @param {String=} sourceInfo.type The type of the source, only needed if using cognosAssetId and the type if the source is changing.
		 * @param {String=} sourceInfo.name Name of the source, only needed if you wish to change the name.
		*/
		relink: function (/*sourceInfo*/) {
			// var dataSourcesSvc = this.services.getSvcSync('dataSources');
			// dataSourcesSvc.relink(this.oDataSource, oNewDataSourceObj);
		},

		_modeIsDisAllowedInMobile(mode) {
			const renderer = this.getFeature('Renderer.internal');
			return renderer && renderer.isMobile() && mode !== 'consumption';
		},

		_togglePropertiesIfNeeded(contentView){
			// Ensure the properties pane is closed in eventGroup or consumption mode
			if (contentView.isEventGroupViewMode || !contentView.isAuthoringMode) {
				this.toggleProperties();
			}
		},

		_shouldToggleEventGroupMode(mode, contentView){
			return (mode === 'eventGroups' && !contentView.isEventGroupViewMode) ||
			(mode !== 'eventGroups' && contentView.isEventGroupViewMode);
		},

		_shouldToggleMode(mode, contentView){
			return (mode === 'authoring' && !contentView.isAuthoringMode) ||
			(mode === 'consumption' && contentView.isAuthoringMode);
		},

		/**
		 * Change the mode between authoring, consumption and eventGroups
		 * @param {string} mode
		 */
		setMode: function(mode) {
			let promise;
			var contentView = this.view;
			if (contentView) {
				if (this._modeIsDisAllowedInMobile(mode)) {
					return Promise.reject(new Error(`${mode} mode is not allowed in a mobile layout.`));
				}
				if (this._shouldToggleEventGroupMode(mode, contentView)) {
					// toggle from/to 'eventGroup'
					promise = contentView.toggleEventGroupMode(mode);
				} else if (this._shouldToggleMode(mode, contentView)) {
					promise = this._changeProfile( mode )
						.then( ()=> {
							return contentView.toggleMode();
						});
				}

				promise = promise || Promise.resolve(true);
				promise.then( (resolvedValue)=> {
					this._togglePropertiesIfNeeded(contentView);
					return resolvedValue;
				});
			}
			return promise || Promise.reject(new Error('Cannot set mode without a content view'));
		},

		getMode: function() {
			var contentView = this.view;
			if (contentView) {
				return contentView.isEventGroupViewMode ? 'eventGroups' : contentView.isAuthoringMode ? 'authoring' : 'consumption';
			}
		},

		/**
		 * Get the dashboard instance specification
		 * @return {Object} dashboard spec
		 */
		getSpec: function() {
			var contentView = this.view;
			if (contentView && contentView.getContent) {
				var content = contentView.getContent();
				return content.boardSpec;
			}
		},

		/**
		 * Undo the last action performed on the dashboard
		 */
		undo: function() {
			var contentView = this.view;
			if (contentView) {
				contentView.doUndo();
			}
		},

		/**
		 * Reverse the last undo performed on the dashboard
		 */
		redo: function() {
			var contentView = this.view;
			if (contentView) {
				contentView.doRedo();
			}
		},

		/**
		 * @depreciated this API is depreciated. Do not use.
		 * Toggle the properties pane
		 * close the pane when in non authoring (consumption and fullscreen) and event group mode
		 * In authoring mode/non event group mode, toggle the properties pane open/close state or switch to the properties pane when another pane is open
		 */
		toggleProperties: function() {
			const contentView = this.view;
			if (contentView) {
				const dashboardAuthoringToolsPane = this.getFeature('InAppSlideoutState');
				if(!dashboardAuthoringToolsPane) {
					return;
				}
				if (contentView.isEventGroupViewMode || !contentView.isAuthoringMode) {
					dashboardAuthoringToolsPane.close();
					return;
				}
				const dashboardState = this.getFeature('DashboardState');
				const propertiesPanelId = 'PropertiesPane';
				if(dashboardAuthoringToolsPane.isOpen()){
					if(dashboardState.getUiState().sidePanel.current === propertiesPanelId) {
						dashboardAuthoringToolsPane.close();
					} else {
						dashboardState.setSidePanelCurrentView(propertiesPanelId);
					}
				} else {
					dashboardState.setSidePanelCurrentView(propertiesPanelId);
					dashboardAuthoringToolsPane.open();
				}
			}
		},

		/**
		* @returns an object containing dashboard information (isDirty, dashboard id and dashboard search path)
		*/
		getDashboardInfo: function() {
			return this.view.getBoardInfo();
		},

		deselectAllWidgets: function() {
			const {
				boardController: {
					layoutController
				} = {}
			} = this.view;

			if (layoutController) {
				return layoutController.getInteractionController()
					.then(controller => {
						controller.selectionHandler.deselectAll();
					});
			}
			return Promise.resolve();
		},

		getToolbarActions: function() {
			const toolbarItems = this.view.boardController
				&& this.view.boardController.layoutController
				&& this.view.boardController.layoutController.interactionController
				&& this.view.boardController.layoutController.interactionController.toolbarItems;

			return toolbarItems;
		},

		/**
		 * get live widget registry
		 *
		 * @return a copy of live widget registry
		 */
		getWidgetRegistry: function(){
			if ( !this.FrozenWidgetRegistry ){
				this.FrozenWidgetRegistry = ObjectHelper.deepFreezeObject(JSON.parse(JSON.stringify(this.widgetRegistry)));
			}
			return this.FrozenWidgetRegistry;
		},

		/**
		 * Copy the selected widget(s) on the dashboard
		 */
		copy: function() {
			var contentView = this.view;
			if (contentView) {
				contentView.doCopy();
			}
		},

		/**
		 * Paste the selected widget(s) on the dashboard
		 */
		paste: function() {
			var contentView = this.view;
			if (contentView) {
				return contentView.doPaste();
			}
			return Promise.reject();
		}
	});

	return DashboardController;
});
